// Per-page SEO keywords: focus keyword + 12-15 short/long-tail variants,
// localized with state/city names. Use in route head() as the "keywords" meta.

type ProductKey =
  | "dscr-loans"
  | "construction-loans"
  | "fix-and-flip-loans"
  | "bridge-loans"
  | "portfolio-rental-loans"
  | "airbnb-short-term-rental-loans"
  | "section-8-loans"
  | "foreign-national-loans"
  | "multifamily-loans"
  | "hard-money-loans";

type Templates = { focus: (loc: string) => string; extras: (loc: string) => string[] };

const PRODUCT_TEMPLATES: Record<ProductKey, Templates> = {
  "dscr-loans": {
    focus: (loc) => `DSCR loans ${loc}`,
    extras: (loc) => [
      `DSCR loan ${loc}`,
      `DSCR mortgage ${loc}`,
      `rental property loans ${loc}`,
      `investment property mortgage ${loc}`,
      `no income verification loan ${loc}`,
      `LLC DSCR loan ${loc}`,
      `30 year DSCR loan ${loc}`,
      `${loc} rental property financing`,
      `DSCR refinance ${loc}`,
      `cash out DSCR loan ${loc}`,
      `Airbnb DSCR loan ${loc}`,
      `Section 8 DSCR ${loc}`,
      `${loc} investor mortgage lender`,
      `best DSCR lender ${loc}`,
    ],
  },
  "construction-loans": {
    focus: (loc) => `construction loans ${loc}`,
    extras: (loc) => [
      `ground up construction loan ${loc}`,
      `spec home construction loan ${loc}`,
      `new build construction financing ${loc}`,
      `${loc} construction lender`,
      `LTC construction loan ${loc}`,
      `ARV construction loan ${loc}`,
      `construction to permanent loan ${loc}`,
      `${loc} builder financing`,
      `residential construction loan ${loc}`,
      `1-4 unit construction loan ${loc}`,
      `infill construction financing ${loc}`,
      `${loc} investor construction loan`,
      `DSCR takeout construction ${loc}`,
      `hard money construction loan ${loc}`,
    ],
  },
  "fix-and-flip-loans": {
    focus: (loc) => `fix and flip loans ${loc}`,
    extras: (loc) => [
      `hard money loan ${loc}`,
      `rehab loan ${loc}`,
      `${loc} flip financing`,
      `bridge loan for flippers ${loc}`,
      `100% rehab financing ${loc}`,
      `after repair value loan ${loc}`,
      `ARV loan ${loc}`,
      `private money lender ${loc}`,
      `short term investor loan ${loc}`,
      `${loc} house flipping loan`,
      `fix and flip lender ${loc}`,
      `real estate investor loan ${loc}`,
      `${loc} rehab bridge loan`,
      `12 month rehab loan ${loc}`,
    ],
  },
  "bridge-loans": {
    focus: (loc) => `bridge loans ${loc}`,
    extras: (loc) => [
      `real estate bridge loan ${loc}`,
      `${loc} bridge lender`,
      `short term bridge financing ${loc}`,
      `12 month bridge loan ${loc}`,
      `investor bridge loan ${loc}`,
      `bridge to DSCR ${loc}`,
      `cash out bridge loan ${loc}`,
      `${loc} private bridge lender`,
      `interest only bridge loan ${loc}`,
      `commercial bridge loan ${loc}`,
      `residential bridge loan ${loc}`,
      `${loc} value add bridge`,
      `bridge refinance ${loc}`,
      `fast close bridge ${loc}`,
    ],
  },
  "portfolio-rental-loans": {
    focus: (loc) => `portfolio rental loans ${loc}`,
    extras: (loc) => [
      `blanket loan ${loc}`,
      `rental portfolio loan ${loc}`,
      `${loc} multi property mortgage`,
      `5+ property DSCR loan ${loc}`,
      `investor blanket mortgage ${loc}`,
      `LLC portfolio loan ${loc}`,
      `SFR portfolio loan ${loc}`,
      `${loc} rental portfolio refinance`,
      `cross collateral loan ${loc}`,
      `${loc} landlord portfolio loan`,
      `commercial rental loan ${loc}`,
      `no seasoning portfolio loan ${loc}`,
      `${loc} scale rental portfolio`,
      `10 property blanket loan ${loc}`,
    ],
  },
  "airbnb-short-term-rental-loans": {
    focus: (loc) => `Airbnb loans ${loc}`,
    extras: (loc) => [
      `short term rental loan ${loc}`,
      `STR DSCR loan ${loc}`,
      `${loc} vacation rental financing`,
      `AirDNA DSCR loan ${loc}`,
      `Vrbo loan ${loc}`,
      `short term rental mortgage ${loc}`,
      `${loc} STR investor loan`,
      `Airbnb refinance ${loc}`,
      `STR cash out loan ${loc}`,
      `${loc} short term rental lender`,
      `1-4 unit STR loan ${loc}`,
      `no tax return STR loan ${loc}`,
      `${loc} Airbnb mortgage lender`,
      `LLC STR financing ${loc}`,
    ],
  },
  "section-8-loans": {
    focus: (loc) => `Section 8 DSCR loans ${loc}`,
    extras: (loc) => [
      `Section 8 rental loan ${loc}`,
      `HUD FMR DSCR loan ${loc}`,
      `${loc} Section 8 investor mortgage`,
      `HAP contract loan ${loc}`,
      `voucher rental financing ${loc}`,
      `Section 8 refinance ${loc}`,
      `${loc} Section 8 landlord loan`,
      `HUD rental DSCR ${loc}`,
      `Housing Choice Voucher loan ${loc}`,
      `${loc} affordable housing investor loan`,
      `Section 8 cash out loan ${loc}`,
      `LLC Section 8 loan ${loc}`,
      `Section 8 portfolio loan ${loc}`,
      `${loc} subsidized rental mortgage`,
    ],
  },
  "multifamily-loans": {
    focus: (loc) => `multifamily loans ${loc}`,
    extras: (loc) => [
      `apartment building loan ${loc}`,
      `5 unit apartment loan ${loc}`,
      `commercial multifamily financing ${loc}`,
      `${loc} multifamily lender`,
      `small balance multifamily loan ${loc}`,
      `LLC apartment loan ${loc}`,
      `multifamily refinance ${loc}`,
      `${loc} apartment building financing`,
      `value add multifamily loan ${loc}`,
      `NOI cap rate loan ${loc}`,
      `${loc} commercial apartment mortgage`,
      `mixed use multifamily loan ${loc}`,
      `multifamily cash out refinance ${loc}`,
      `${loc} investor apartment loan`,
    ],
  },
  "hard-money-loans": {
    focus: (loc) => `hard money loans ${loc}`,
    extras: (loc) => [
      `hard money lender ${loc}`,
      `private money loan ${loc}`,
      `${loc} private lender real estate`,
      `asset based real estate loan ${loc}`,
      `fast close investor loan ${loc}`,
      `distressed property loan ${loc}`,
      `${loc} auction financing investor`,
      `cash out hard money ${loc}`,
      `land loan investor ${loc}`,
      `${loc} no income verification investor loan`,
      `short term private financing ${loc}`,
      `${loc} rehab hard money loan`,
      `partner buyout loan ${loc}`,
      `${loc} private money mortgage lender`,
    ],
  },
  "foreign-national-loans": {
    focus: (loc) => `foreign national loans ${loc}`,
    extras: (loc) => [
      `foreign national mortgage ${loc}`,
      `ITIN loan ${loc}`,
      `non US citizen mortgage ${loc}`,
      `foreign investor loan ${loc}`,
      `${loc} foreign national DSCR`,
      `no US credit mortgage ${loc}`,
      `foreign buyer loan ${loc}`,
      `${loc} non resident mortgage`,
      `foreign national rental loan ${loc}`,
      `LLC foreign national loan ${loc}`,
      `${loc} foreign investor lender`,
      `foreign national refinance ${loc}`,
      `Canadian investor loan ${loc}`,
      `${loc} international buyer mortgage`,
    ],
  },
};

function build(product: string, loc: string): { focus: string; keywords: string[] } {
  const t = PRODUCT_TEMPLATES[product as ProductKey];
  if (!t) return { focus: `${product} ${loc}`, keywords: [] };
  const focus = t.focus(loc);
  const extras = t.extras(loc);
  // dedupe, keep focus first, cap at 15
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of [focus, ...extras]) {
    const key = k.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(k);
    if (out.length >= 15) break;
  }
  return { focus, keywords: out };
}

export function stateKeywords(productSlug: string, stateName: string) {
  return build(productSlug, stateName);
}

export function cityKeywords(productSlug: string, cityName: string, stateName: string) {
  return build(productSlug, `${cityName}, ${stateName}`);
}

export function stateKeywordsMeta(productSlug: string, stateName: string): string {
  return stateKeywords(productSlug, stateName).keywords.join(", ");
}

export function cityKeywordsMeta(productSlug: string, cityName: string, stateName: string): string {
  return cityKeywords(productSlug, cityName, stateName).keywords.join(", ");
}
